import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { DataserviceService } from 'src/app/dataservice.service';
import { NzModalRef, NZ_MODAL_DATA } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';

@Component({
  selector: 'app-pin-modal',
  templateUrl: './pin-modal.component.html',
  styleUrl: './pin-modal.component.css'
})
export class PinModalComponent {

  leadForm: FormGroup;
  dispForm: FormGroup;
  noteForm: FormGroup;

  currentUser: any;
  states: any[] = [];
  dispositions: any[] = [];
  notes: any[] = [];
  pin_history: any[] = [];
  segment = 'info'
  area: any;
  address: any;
  pinData: any

  customFieldDefs: any[] = [];
  customFieldValues: { [key: string]: any } = {};

  addressCopied = false;

  disableFutureDates = (current: Date): boolean => {
    return current && current > new Date();
  };

  constructor(
    @Inject(NZ_MODAL_DATA) private data: any,
    private dataService: DataserviceService,
    private modalRef: NzModalRef,
    private notification: NzNotificationService
  ) {

    this.leadForm = new FormGroup({
      first: new FormControl(null, [this.nameFormatValidator.bind(this)]),
      last: new FormControl(null, [this.nameFormatValidator.bind(this)]),
      phone: new FormControl(null),
      email: new FormControl(null, [this.optionalEmailValidator.bind(this)]),
      street: new FormControl(null),
      street_line_2: new FormControl(null),
      city: new FormControl(null),
      state: new FormControl(null),
      zip: new FormControl(null),
      dob: new FormControl('')
    });

    this.dispForm = new FormGroup({
      disposition: new FormControl(null, [Validators.required])
    });

    this.noteForm = new FormGroup({
      note: new FormControl(null, [Validators.required])
    });

  }

  get showCustomFields(): boolean {
    if (!this.customFieldDefs?.length) return false;
    if (!this.data?.data?.company_id) return true;
    const pinCompany = this.data.data.company_id;
    return pinCompany === this.currentUser?.company_id || pinCompany === this.currentUser?.active_company_name;
  }

  get latestUserNote(): any {
    const userNotes = (this.pin_history || []).filter(
      (item: any) => (item.history_type || 'user added') === 'user added'
    );
    return userNotes[0] || null;
  }

  nameFormatValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.trim();
    const regex = /^[a-zA-Z\u00C0-\u017F'-. ]{1,25}$/;

    if (!value) return null;

    if (!regex.test(value)) {
      return { invalidNameFormat: true };
    }

    return null;
  }

  optionalEmailValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (!value) return null;

    const error = Validators.email(control);
    return error ? error : null;
  }

  ngOnInit() {
    this.currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    this.states = this.dataService.states;
    this.dispositions = this.data.dispositions;

    this.pin_history = this.data?.data?.pin_history?.sort((a: any, b: any) => { return new Date(b.date).getTime() - new Date(a.date).getTime(); }) || [];
    this.notes = JSON.parse(this.data?.data?.notes || '[]');

    if (this.data.data) {
      this.pinData = this.data;
      this.setData();
      this.loadCustomFieldDefinitions();
    } else {
      this.splitAddress();
      this.loadCustomFieldDefinitions();
    }

    if(this.data.mdu) {
      this.leadForm.get('street_line_2')?.setValidators([Validators.required])
    }
  }

  loadCustomFieldDefinitions() {
    const companyId = this.data?.data?.company_id || this.currentUser?.company_id;
    if (!companyId) return;
    this.dataService.getData('pin-management/fields-by-company/' + companyId).subscribe({
      next: (res: any) => {
        this.customFieldDefs = (res?.data || []).filter((f: any) => f.status === 'visible');
      },
      error: () => {
        this.customFieldDefs = [];
      }
    });
  }

splitAddress() {
  const rawAddress = this.data?.address || "";
  
  // 1. Extract ZIP
  const zipMatch = rawAddress.match(/\d{5}(-\d{4})?$/);
  const zip = zipMatch ? zipMatch[0] : null;
  let remaining = zip ? rawAddress.replace(zip, '').trim() : rawAddress;

  // 2. Extract State (Looking for 2-letter codes or matches in your list)
  // We remove trailing commas if they exist
  remaining = remaining.replace(/,$/, '').trim(); 
  const parts = remaining.split(/[\s,]+/); // Split by spaces or commas
  const stateCandidate = parts.pop(); // The last word should be the State (NH)
  
  const state = this.states?.find((s: any) => 
    s?.name?.toLowerCase() === stateCandidate?.toLowerCase() || 
    s?.abbr?.toLowerCase() === stateCandidate?.toLowerCase()
  );

  // 3. Extract City (The word before the state)
  const city = parts.pop(); // The word before NH (GORHAM)

  // 4. Everything else is the Street
  const street = parts.join(' '); // 11 CASCADE FLT

  // Update Form
  this.leadForm.patchValue({
    street: street || null,
    city: city || null,
    state: state?.abbr || stateCandidate || null,
    zip: zip
  });
}


  setData() {
    if (this.data.data.disposition_id) {
      const disp = this.dispositions.find((disp: any) => disp.id == this.data.data.disposition_id)
      this.dispForm.patchValue({ disposition: disp });
    }

    let state;

    if (this.data?.data?.address_state?.length > 2) {
      state = this.states.find((s: any) => s.label.toLowerCase() == this.data?.data?.address_state.toLowerCase())
    } else {
      state = this.data?.data?.address_state;
    }

    this.address = this.data.data.address_line_1 + ' ' + this.data.data.address_line_2 + ' ' + this.data.data.address_city + ', ' + this.data?.data?.address_state + ' ' + this.data.data.address_zip

    this.leadForm.patchValue({
      first: this.data?.data?.customer_data.first_name || null,
      last: this.data?.data?.customer_data.last_name || null,
      phone: this.data?.data?.customer_data.phone_number || null,
      email: this.data?.data?.customer_data.email || null,
      street: this.data.data.address_line_1,
      city: this.data.data.address_city,
      state: state?.value || state,
      zip: this.data.data.address_zip,
      dob: this.data?.data?.customer_data.date_of_birth || null
    });

    this.customFieldValues = { ...(this.data?.data?.customer_data?.custom_field_values || {}) };
  }

  segmentChange(event: any) {
    this.segment = event;
  }

  buildCustomerData() {
    const base = {
      first_name: this.leadForm.get('first')?.value || null,
      last_name: this.leadForm.get('last')?.value || null,
      email: this.leadForm.get('email')?.value || null,
      phone_number: (this.leadForm.get('phone')?.value || '').replace(/[\(\)\-\s]/g, ''),
      date_of_birth: this.leadForm.get('dob')?.value || null,
      custom_field_values: { ...this.customFieldValues }
    };
    return base;
  }

  hasDataActuallyChanged(): boolean {
    const customerFields = ['first', 'last', 'email', 'phone', 'dob'];
    
    return customerFields.some(field => this.leadForm.get(field)?.dirty);
  }

  async geocodeAddress() {
    const parts = this.address?.split(',') || [];
    const statePart = parts[2]?.trim()?.split(' ');
    const state = statePart?.[0] ? this.states?.find((s: any) => s?.name?.toLowerCase() === statePart[0].toLowerCase()) : null;
  
    const address = `${parts[0]} ${this.leadForm.get('street_line_2')?.value} ${parts[1]}, ${state?.abbr} ${statePart?.[1]}`

    this.dataService.getLatLng(address)
      .subscribe({
        next: async (res: any) => {

          if(res.features?.[0].properties?.context?.secondary_address?.name) {
            this.leadForm.get('street_line_2')?.setValue(res.features?.[0].properties?.context?.secondary_address?.name)
          }

          await this.createNewPin();
        }
      })
  }

  async determineSaveType() {
    if(this.pinData?.disposition_id == this.dispForm.get('disposition')?.value?.id && !this.hasDataActuallyChanged()) {
      this.saveNote();
    }else if(!this.pinData?.data?.id && !this.data.mdu){
      this.createNewPin();
    }else if(!this.pinData?.data?.id && this.data.mdu) {
      await this.geocodeAddress()
    }else {
      this.update();
    }
  }

  createNewPin() {
    const obj = {
      customerData: this.buildCustomerData(),
      address_line_1: this.leadForm.get('street')?.value || '',
      address_line_2: this.leadForm.get('street_line_2')?.value || '',
      address_city: this.leadForm.get('city')?.value || '',
      address_state: this.leadForm.get('state')?.value || '',
      address_zip: this.leadForm.get('zip')?.value || '',
      latitude: this.data.coords.latitude,
      longitude: this.data.coords.longitude,
      disposition_id: this.dispForm.get('disposition')?.value?.id,
      pin: this.dispForm.get('disposition')?.value?.pin,
      areaId: this.area || null,
      provider: this.data?.provider,
      mdu: this.data.mdu
    }

    this.dataService.postData(obj, 'pins/create-pin')
      .subscribe({
        next: (res: any) => {

          this.data.data = {
            ...
            this.data?.data || {},
            id: res?.data,
            company_id: this.data.company,
            provider_id: this.data.provider
          }

          this.saveNote();
          this.modalRef.close({ disposition_id: obj.disposition_id, pin: obj.pin, res: res?.data })
        },
        error: (err: any) => {
          console.log('error ->', err)
        }
      })
  }

  update() {

    const obj = {
      customerData: this.buildCustomerData(),
      disposition_id: this.dispForm.get('disposition')?.value?.id,
      id: this.data.data.id,
      pin: this.dispForm.get('disposition')?.value?.pin,
      provider_id: this.data.data.provider_id,
      company_id: this.data.data.company_id,
      text: `Pin updated with disposition ${this.dispForm.get('disposition')?.value?.disposition}`,
      areaId: this.area || null
    }

    this.dataService.postData(obj, 'pins/update')
      .subscribe({
        next: (res: any) => {
          this.saveNote();
          this.modalRef.close({ res: this.data.data.id, disposition_id: obj.disposition_id, pin: obj.pin })
        },
        error: (err: any) => {
          console.log('error ->', err)
        }
      })
  }

  saveNote() {
    if (this.noteForm.valid) {
      const obj = {
        customerData: this.buildCustomerData(),
        disposition_id: this.dispForm.get('disposition')?.value?.id,
        id: this.data.data.id,
        pin: this.dispForm.get('disposition')?.value?.pin,
        provider_id: this.data.data.provider_id,
        company_id: this.data.data.company_id,
        text: this.noteForm.get('note')?.value,
        areaId: this.area || null
      }
      this.dataService.postData(obj, 'pins/update')
        .subscribe({
          next: (res: any) => {
            this.pin_history = this.pin_history ? this.pin_history : [];
            this.pin_history.unshift({
              note: this.noteForm.get('note')?.value,
              date: new Date(),
              user: `${this.currentUser.first_name} ${this.currentUser.last_name}`,
              history_type: 'user added'
            });
            this.noteForm.reset()
          },
          error: (err: any) => {
            console.log('error ->', err)
          }
        })
    }
  }

  cancel() {
    this.modalRef.close()
  }

  async copyAddress() {
    try {
      await navigator.clipboard.writeText(this.address || '');
      this.addressCopied = true;
      this.Notification('success', 'Address copied to clipboard', 'Success');
      setTimeout(() => (this.addressCopied = false), 2000);
    } catch (error) {
      this.Notification('error', 'Error copying address', 'Error');
    }
  }

  Notification(type: string, message: string, title: string) {
    try {
      this.notification.create(
        type,
        title,
        message,
      )
    } catch (error) {
      console.log(error)

    }
  }

}